
interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  color: string;
  size: string;
  quantity: number;
  price: number;
  image?: string;
}

interface Order {
  id: string;
  orderNumber: string;
  createdBy: string;
  createdByName: string;
  createdByRole: 'Manager' | 'Partner';
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  notes: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'processing' | 'shipping' | 'delivered' | 'returned' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

interface OrderDetailProps {
  order: Order;
  onClose: () => void;
  onEdit?: () => void;
  onStatusChange: (orderId: string, status: Order['status']) => void;
  currentUser: {
    id: string;
    name: string;
    role: 'Manager' | 'Partner';
    commissionRate: number;
  };
  canEdit: boolean;
}

export const OrderDetail = ({
  order,
  onClose,
  onEdit,
  onStatusChange,
  currentUser,
  canEdit
}: OrderDetailProps) => {
  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipping':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'returned':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'Chưa xử lý';
      case 'processing':
        return 'Đang xử lý';
      case 'shipping':
        return 'Đang giao hàng';
      case 'delivered':
        return 'Đã giao hàng';
      case 'returned':
        return 'Đơn hoàn trả';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'ri-time-line';
      case 'processing':
        return 'ri-settings-3-line';
      case 'shipping':
        return 'ri-truck-line';
      case 'delivered':
        return 'ri-checkbox-circle-line';
      case 'returned':
        return 'ri-arrow-go-back-line';
      case 'cancelled':
        return 'ri-close-circle-line';
      default:
        return 'ri-information-line';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canChangeStatus = (newStatus: Order['status']) => {
    // Manager có thể thay đổi tất cả trạng thái
    if (currentUser.role === 'Manager') return true;

    // Partner chỉ có thể thay đổi trạng thái đơn hàng của mình
    if (order.createdBy !== currentUser.id) return false;

    // Partner không thể thay đổi sang trạng thái returned (chỉ Manager mới được)
    if (newStatus === 'returned') return false;

    return true;
  };

  const getTimeRemaining = () => {
    const now = new Date();
    const createdAt = new Date(order.createdAt);
    const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    const hoursRemaining = 24 - hoursDiff;

    if (hoursRemaining <= 0) return null;

    return Math.floor(hoursRemaining);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Chi tiết đơn hàng</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)} flex-shrink-0`}>
              <i className={`${getStatusIcon(order.status)} mr-1`}></i>
              {getStatusText(order.status)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Thông tin đơn hàng */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-blue-600"></i>
                  Thông tin đơn hàng
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-600">Mã đơn hàng:</span>
                    <span className="font-medium">{order.orderNumber}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-600">Ngày tạo:</span>
                    <span className="font-medium">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-600">Cập nhật lần cuối:</span>
                    <span className="font-medium">{formatDate(order.updatedAt)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-600">Người tạo:</span>
                    <span className="font-medium">
                      {order.createdByName} ({order.createdByRole})
                    </span>
                  </div>
                  {canEdit && order.status === 'pending' && (
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 pt-2 border-t border-gray-200">
                      <span className="text-gray-600">Thời gian chỉnh sửa:</span>
                      <span className="font-medium text-orange-600">
                        {getTimeRemaining() ? `Còn ${getTimeRemaining()} giờ` : 'Hết hạn'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-user-line text-green-600"></i>
                  Thông tin khách hàng
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-600">Tên khách hàng:</span>
                    <span className="font-medium">{order.customerName}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-gray-600">Số điện thoại:</span>
                    <span className="font-medium">{order.customerPhone}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-gray-600 block mb-1">Địa chỉ giao hàng:</span>
                    <span className="font-medium">{order.customerAddress}</span>
                  </div>
                  {order.notes && (
                    <div className="pt-2 border-t border-gray-200">
                      <span className="text-gray-600 block mb-1">Ghi chú:</span>
                      <span className="font-medium">{order.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Danh sách sản phẩm */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-shopping-bag-line text-purple-600"></i>
                Sản phẩm đã đặt ({order.items.length} sản phẩm)
              </h3>
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border border-gray-200 rounded-lg bg-white">
                    <img
                      src={item.image || 'https://public.readdy.ai/ai/img_res/18e1312b59cd5bc62b9a11f20944903b.jpg'}
                      alt={item.productName}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900">{item.productName}</h4>
                      <p className="text-sm text-gray-600">
                        Màu: <span className="font-medium">{item.color}</span> | 
                        Size: <span className="font-medium">{item.size}</span>
                      </p>
                      <p className="text-sm text-blue-600 font-medium">
                        {formatCurrency(item.price)} × {item.quantity}
                      </p>
                    </div>
                    <div className="text-left sm:text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tổng kết đơn hàng */}
            <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Tổng giá trị đơn hàng</h3>
                  <p className="text-sm text-gray-600">
                    {order.items.reduce((total, item) => total + item.quantity, 0)} sản phẩm
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">
                    {formatCurrency(order.totalAmount)}
                  </p>
                </div>
              </div>
            </div>

            {/* Thay đổi trạng thái */}
            {(order.createdBy === currentUser.id || currentUser.role === 'Manager') && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-refresh-line text-orange-600"></i>
                  Thay đổi trạng thái đơn hàng
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {[
                    { status: 'pending', label: 'Chưa xử lý', icon: 'ri-time-line' },
                    { status: 'processing', label: 'Đang xử lý', icon: 'ri-settings-3-line' },
                    { status: 'shipping', label: 'Đang giao hàng', icon: 'ri-truck-line' },
                    { status: 'delivered', label: 'Đã giao hàng', icon: 'ri-checkbox-circle-line' },
                    { status: 'returned', label: 'Đơn hoàn trả', icon: 'ri-arrow-go-back-line' },
                    { status: 'cancelled', label: 'Đã hủy', icon: 'ri-close-circle-line' }
                  ].map(({ status, label, icon }) => (
                    <button
                      key={status}
                      onClick={() => canChangeStatus(status as Order['status']) && onStatusChange(order.id, status as Order['status'])}
                      disabled={!canChangeStatus(status as Order['status']) || order.status === status}
                      className={`p-2 sm:p-3 rounded-lg border text-xs sm:text-sm font-medium transition-colors ${
                        order.status === status
                          ? 'bg-blue-100 border-blue-300 text-blue-800'
                          : canChangeStatus(status as Order['status'])
                          ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <i className={`${icon} mr-1`}></i>
                      <span className="hidden sm:inline">{label}</span>
                      <span className="sm:hidden">{label.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
                {currentUser.role === 'Partner' && (
                  <p className="text-xs text-gray-500 mt-2">
                    * Chỉ Manager mới có thể chuyển đơn hàng sang trạng thái "Đơn hoàn trả"
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              Đóng
            </button>
            {onEdit && canEdit && (order.createdBy === currentUser.id || currentUser.role === 'Manager') && (
              <button
                onClick={onEdit}
                className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105 hover:shadow-lg whitespace-nowrap"
              >
                <i className="ri-edit-line mr-1"></i>
                Chỉnh sửa đơn hàng
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
